import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import {
  EnumService,
  LocalStorageService,
  NameIdPair,
  SessionManagerService,
  CompanyService,
  NameFilter,
  CostItem,
} from '@ops/shared-types';

@Injectable({
  providedIn: 'root',
})
export class ProductionCostService implements OnDestroy {
  paidByOptions: Array<{
    method: string;
    pairList: Array<{ id: string; name: string }>;
    optionsList: Array<string>;
  }> = [];
  selectedCompanyId: string;
  selectedCompany$: Observable<NameFilter>;
  private companySubscription: Subscription;

  assignToValues: Array<NameIdPair>;

  estimateItems: Array<CostItem>;

  constructor(
    private enumService: EnumService,
    private session: SessionManagerService,
    private ls: LocalStorageService,
    private companyService: CompanyService
  ) {
    this.selectedCompany$ = this.companyService.selectedCompany$.asObservable();
    
    // Subscribe to company changes and refetch payment methods
    this.companySubscription = this.selectedCompany$.subscribe(company => {
      if (company?._id && company._id !== this.selectedCompanyId) {
        this.selectedCompanyId = company._id;
        this.refreshPaymentMethodsForCompany(company._id);
      }
    });

    setTimeout(() => {
      this.getAllPaidByOptions();
      this.setAssignToValues();
    }, 500);
  }

  public async setAssignToValues(): Promise<void> {
    const companyId = await this.getCompanyIdHack();

    if (!companyId) {
      return;
    }
    this.assignToValues = await this.enumService.getAssignToOptions(companyId);
  }

  private async getCompanyIdHack(): Promise<string | null> {
    const companyId = this.ls.getCurrentCompany()?._id;
    if (!companyId) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await this.getCompanyIdHack();
          resolve(result);
        }, 100);
      });
    }
    return companyId;
  }

  getEstimateItems(): Array<CostItem> {
    return this.estimateItems;
  }

  setEstimateItems(estimateItems: Array<CostItem>) {
    this.estimateItems = estimateItems;
  }

  private async getAllPaidByOptions(companyId?: string): Promise<void> {
    this.paidByOptions = [];
    const c = await this.ls.getCurrentCompanyInRetryLoop();
    const company = companyId || c?._id;

    const paymentMethodOptions = this.enumService.getAllPaymentMethodOptions();

    for await (const m of paymentMethodOptions) {
      const pair = await this.enumService.getPaidByOptions(m, company);
      this.paidByOptions.push({
        method: m,
        pairList: pair.paidByList,
        optionsList: pair.optionsList,
      });
    }
  }

  async refreshPaidByOptions(): Promise<void> {
    await this.getAllPaidByOptions();
  }

  private async refreshPaymentMethodsForCompany(companyId: string): Promise<void> {
    try {
      // Clear EnumService cache for the new company
      this.enumService.clearCache(companyId);
      
      // Clear existing options
      this.paidByOptions = [];
      
      // Reload payment methods for the new company
      await this.getAllPaidByOptions(companyId);
      await this.setAssignToValues();
    } catch (error) {
      console.error('Error refreshing payment methods for company:', error);
    }
  }

  selectCompany(companyFilter: NameFilter): void {
    this.selectedCompanyId = companyFilter._id;
    this.companyService.selectedCompany$.next(companyFilter);
    this.refreshPaymentMethodsForCompany(companyFilter._id);
  }

  ngOnDestroy(): void {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  getpaidByOptionsForPaymentMethod(paymentMethod: string) {
    if (!paymentMethod) return;
    const result = this.paidByOptions.find((o) => o.method === paymentMethod);
    return {
      pairList: result?.pairList,
      optionsList: result?.optionsList,
    };
  }
}